import { useEffect, useMemo, useState } from "react";
import { Form, Input, Button, Row, Col, Radio, Select, Card } from "antd";
import { AddressControl, eventCreationModeEnum, util } from "@seims/shared";
import eventsService from "../../../services/eventsService";
import lookupService from "../../../services/lookupService";
import { openNotification } from "../../custom";
import { Notify } from "../../../services/custom/Messages";
import { APPLICATION_NAMES, VENUE_TYPES_STORE_NAME } from "../../../constants";
import { levelsEnum, recordError } from "../../../utils/logService";

export default function VenueForm({ onSave, onCancel, eventId = undefined }) {
  const [form] = Form.useForm();

  const selectedVenueTypeValue = Form.useWatch("venueType", form);
  const [address, setAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [isLocal, setIsLocal] = useState(true);
  const [venueTypes, setVenueTypes] = useState([]);
  
  const otherVenueType = useMemo(() => {
    return Number(venueTypes.find(
    (t) => t.value?.toLowerCase() === "other venue"
  )?.key)
  }, [venueTypes]);
  
  

  // Load venue types
  useEffect(() => {
    const loadTypes = async () => {
      const res = await lookupService.getLookup(VENUE_TYPES_STORE_NAME, { useCache: true });
      setVenueTypes(res?.data ?? []);
    };
    loadTypes();
  }, []);

  // Handle address save
  const handleAddressSubmit = (values) => {
    const formatted = {
      ...values,
      country: values.country?.value ?? values.country,
      province: values?.province?.value ?? values?.province,
    };

    setAddress(formatted);
    setShowAddressModal(false);
  };

  const handleCancelClick = () => {
    form.resetFields();          //  clear form fields
    setAddress(null);            //  clear address
    setShowAddressModal(false);  //  close modal

    onCancel();                  //  notify parent
  };

  // CREATE VENUE 
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (!address) {
        openNotification(
          "Venue",
          "Please provide an address",
          Notify.WARNING
        );
        return;
      }

      const payload = {
        eventId: eventId,
        name: values.name,
        venueType: Number(values.venueType),
        otherType: values?.otherType ?? null,
        address: {
          street: address.street,
          cityOrTown: address.cityOrTown,
          postalCode: address.postalCode,
          province: address.province,
          country: address.country,
          x: address?.x,
          y: address?.y,
        },
      };

      const res = await eventsService.createEventVenue(payload);

      if (!res) throw new Error("Failed");

      const venueId = res?.data?.id;

      // fetch full venue object
      const venueRes = await eventsService.genEventVenueById(venueId);

      if (venueRes?.status === 200) {
        const newVenue = venueRes.data;

        openNotification(
          "Venue",
          "Venue created successfully",
          Notify.SUCCESS
        );

        // SEND BACK TO PARENT
        onSave(newVenue);

        // reset form
        form.resetFields();
        setAddress(null);
      } else {
        throw new Error("Fetch failed");
      }
    } catch (error) {
      recordError(error, levelsEnum.ERROR, APPLICATION_NAMES.EVENTS_APP);
      openNotification(
        "Venue",
        "Failed to create venue",
        Notify.ERROR
      );
    }
  };

  const onNameBlur = (e) => {
        const { value, name } = e.target;
        const textValue = util.toPascalText(value);
        form.setFieldsValue({ [name]: textValue });
      }
    

  return (
    <>
      <Form form={form} layout="vertical">

        {/* VENUE NAME + TYPE */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Venue Name"
              rules={[{ required: true, message: "Venue name is required" }]}
            >
              <Input name="name" placeholder="Enter venue name" onBlur={onNameBlur} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="venueType"
              label="Venue Type"
              rules={[{ required: true, message: "Venue type is required" }]}
            >
              <Select placeholder="Select venue type">
                {venueTypes.map((vt) => (
                  <Select.Option key={vt.key} value={vt.key}>
                    {vt.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={24} md={{ span: 12, offset: 12 }}>
            {selectedVenueTypeValue === otherVenueType && (
              <Form.Item
                name="otherType"
                label="Please specify"
                rules={[{ required: true, message: "Please specify the other venue type" }]}
              >
                <Input/>
              </Form.Item>
          )}
          </Col>
        </Row>

        {/* ADDRESS OPTION */}
        <Form.Item
          name="addressOption"
          label="Address Details"
          rules={[{ required: true, message: "Address is required" }]}
        >
          <Radio.Group
            disabled={!!address}
            options={[
              { label: "Local", value: "local" },
              { label: "Foreign", value: "foreign", disabled: true },
            ]}
            onChange={(e) => {
              const value = e.target.value;
              setIsLocal(value === "local");
              setShowAddressModal(true);
            }}
          />
        </Form.Item>

        {/* ADDRESS PREVIEW */}
        {address && (
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Card
                style={{
                  marginTop: 16,
                  backgroundColor: "#fafafa",
                  borderColor: "#ccc",
                }}
                actions={[
                  <Button key="edit" type="default" onClick={() => setShowAddressModal(true)}>
                    Edit
                  </Button>,
                ]}
              >
                {address && (
                  <Card.Meta
                    title={isLocal ? "Local Address" : "Foreign Address"}
                    description={`${address.street} ${address.cityOrTown} ${address.postalCode}, ${address.country}`}
                  />
                )}
              </Card>
            </Col>
          </Row>
        )}
        {/* ACTIONS */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <Button onClick={handleCancelClick} style={{ marginRight: 8 }}>
            Cancel
          </Button>

          <Button type="primary" onClick={handleSubmit}>
            Save Venue
          </Button>
        </div>
      </Form>

      {/* ADDRESS MODAL */}
      <AddressControl
        visible={showAddressModal}
        title="Add Address"
        isLocal={isLocal}
        onCancel={() => {
          setShowAddressModal(false);
          // Clear radio selection

          // Only clear radio if NO saved address
          if (!address) {
            form.setFieldsValue({ addressOption: null });
          }

        }}
        requireSearchFirst={true}
        onModalSubmit={handleAddressSubmit}
        address={address}
      />
    </>
  );
}
